import React, { Component } from 'react';
import { connect } from 'react-redux';

import { Row, Col, Button } from 'reactstrap';

import { callApi } from '../../utils';
import { showError } from '../../actions/notificationActions';

import FilterBuilder from '../FilterBuilder';
import FilterPanel, { whereToConditions } from '../SavedFilters/FilterPanel';
import { createSavedFilter } from '../../services/savedFiltersApi';
import { PageHeader } from '../DataTable';
import RequestTable from './Table';
import { REQUEST_FIELDS } from './constants';
import { serializeWhere, toWhereQuery } from './serializeWhere';

// ─── URL <-> filter state helpers (matches Users/List.js convention) ──────────

function encodeConditions(conditions) {
  return encodeURIComponent(
    JSON.stringify(conditions.map(({ id, ...rest }) => rest))
  );
}

function parseUrlParams(search) {
  const params = new URLSearchParams(search);
  let conditions = [];
  let logicalOp = '$and';

  const raw = params.get('c');
  if (raw) {
    try {
      const parsed = JSON.parse(decodeURIComponent(raw));
      conditions = parsed.map((c, i) => ({ ...c, id: Date.now() + i }));
    } catch (_) {}
  }

  const op = params.get('op');
  if (op === '$and' || op === '$or') logicalOp = op;

  return { conditions, logicalOp };
}

class List extends Component {
  constructor(props) {
    super(props);
    const { conditions, logicalOp } = parseUrlParams(
      props.location ? props.location.search : ''
    );
    this.state = {
      rows: [], // accumulated across keyset pages
      conditions,
      logicalOp,
      limit: 30,
      // Ordering is fixed server-side to id DESC and pagination is forward-only.
      // "Load more" appends the next keyset page using meta.next_cursor.
      nextCursor: null,
      hasMore: false,
      loading: false, // initial / reset load
      loadingMore: false, // appending the next page
      error: null,
      activeSavedFilter: null,
      builderKey: 0,
    };
  }

  componentDidMount() {
    // Records are only fetched once a filter is active — with no filter we show
    // the saved-filters panel instead (mirrors Users/Orders/ProviderEvent).
    if (this.state.conditions.length > 0) {
      this.load();
    }
  }

  syncUrl = (conditions, logicalOp) => {
    const params = new URLSearchParams();
    if (conditions.length > 0) {
      params.set('c', encodeConditions(conditions));
      params.set('op', logicalOp);
    }
    // The cursor is intentionally NOT URL-persisted — keyset cursors are opaque
    // row ids, not shareable page numbers.
    const search = params.toString();
    const current = this.props.location
      ? this.props.location.search.replace(/^\?/, '')
      : '';
    if (search !== current) {
      this.props.history.replace({ search: search ? `?${search}` : '' });
    }
  };

  // Query string for one keyset page. The same `where` + `limit` ride along on
  // every page fetch; only the cursor changes.
  buildQs = (cursor) => {
    const { conditions, logicalOp, limit } = this.state;
    const whereQ = toWhereQuery(conditions, logicalOp);
    return [
      cursor != null ? `cursor=${encodeURIComponent(cursor)}` : '',
      `limit=${limit}`,
      whereQ,
    ]
      .filter(Boolean)
      .join('&');
  };

  // Initial / reset load: no cursor, replaces the accumulated rows.
  load = () => {
    this.setState({
      loading: true,
      error: null,
      rows: [],
      nextCursor: null,
      hasMore: false,
    });
    callApi(`/requests?${this.buildQs(null)}`)
      .then(({ data = [], meta = {} }) => {
        this.setState({
          loading: false,
          rows: data,
          nextCursor: meta.next_cursor != null ? meta.next_cursor : null,
          hasMore: !!meta.has_more,
        });
        setTimeout(() => window.scrollTo(0, 0), 200);
      })
      .catch((err) => {
        this.setState({
          loading: false,
          error: (err && err.message) || 'Unable to load requests.',
        });
        this.props.dispatch(showError(err));
      });
  };

  // Append the next keyset page using meta.next_cursor.
  loadMore = () => {
    const { nextCursor, hasMore, loadingMore } = this.state;
    if (loadingMore || !hasMore || nextCursor == null) return;

    this.setState({ loadingMore: true });
    callApi(`/requests?${this.buildQs(nextCursor)}`)
      .then(({ data = [], meta = {} }) => {
        this.setState((prev) => ({
          loadingMore: false,
          rows: prev.rows.concat(data),
          nextCursor: meta.next_cursor != null ? meta.next_cursor : null,
          hasMore: !!meta.has_more,
        }));
      })
      .catch((err) => {
        // Keep the rows already loaded; just surface the error as a toast.
        this.setState({ loadingMore: false });
        this.props.dispatch(showError(err));
      });
  };

  // Clear accumulated rows without fetching — the saved-filters panel takes over.
  clearRows = () =>
    this.setState({
      rows: [],
      nextCursor: null,
      hasMore: false,
      error: null,
    });

  handleFilterChange = (_where, conditions, logicalOp) => {
    this.setState({ conditions, logicalOp, activeSavedFilter: null }, () => {
      this.syncUrl(conditions, logicalOp);
      if (conditions.length > 0) {
        this.load();
      } else {
        this.clearRows();
      }
    });
  };

  handleSaveFilter = ({ label, description }) => {
    const { conditions, logicalOp } = this.state;
    const where = serializeWhere(conditions, logicalOp);
    if (Object.keys(where).length === 0) return;
    createSavedFilter('requests', {
      type: 'custom',
      label,
      description,
      value: JSON.stringify(where),
    })
      .then(({ data }) => {
        this.setState({ activeSavedFilter: data });
      })
      .catch((err) => {
        this.props.dispatch(showError(err));
      });
  };

  handleApplySavedFilter = (filter) => {
    const parsed = whereToConditions(filter.value, REQUEST_FIELDS);
    const conditions = parsed.conditions.map((c, i) => ({
      ...c,
      id: Date.now() + i,
    }));
    const logicalOp = parsed.logicalOp;
    this.setState(
      (prev) => ({
        conditions,
        logicalOp,
        activeSavedFilter: filter,
        builderKey: prev.builderKey + 1,
      }),
      () => {
        this.syncUrl(conditions, logicalOp);
        if (conditions.length > 0) this.load();
      }
    );
  };

  handleSavedFilterUpdated = (filter) => {
    this.setState(function (prev) {
      var active = prev.activeSavedFilter;
      return {
        activeSavedFilter: active && active.id === filter.id ? filter : active,
      };
    });
  };

  handleDeletedSavedFilter = (filterId) => {
    this.setState(function (prev) {
      var active = prev.activeSavedFilter;
      return {
        activeSavedFilter: active && active.id === filterId ? null : active,
      };
    });
  };

  clearFilters = () => {
    this.setState(
      (prev) => ({
        conditions: [],
        logicalOp: '$and',
        activeSavedFilter: null,
        builderKey: prev.builderKey + 1,
      }),
      () => {
        this.syncUrl([], '$and');
        this.clearRows();
      }
    );
  };

  view = (id) => {
    this.props.history.push(`/requests/${id}`);
  };

  render() {
    const {
      rows,
      conditions,
      logicalOp,
      loading,
      loadingMore,
      error,
      hasMore,
      activeSavedFilter,
      builderKey,
    } = this.state;
    const hasFilter = conditions.length > 0;
    const hasResults = rows.length > 0;

    return (
      <div className="container-fluid">
        <Row className="mt-3 mt-md-4">
          <Col xs={12}>
            <PageHeader
              title="VEND REQUESTS"
              subtitle={
                hasFilter
                  ? `Filtered results — ${rows.length} loaded${
                      hasMore ? ' (more available)' : ''
                    }`
                  : 'Apply a filter to search vend requests'
              }
            >
              {hasFilter && (
                <button
                  className="btn btn-sm btn-outline-secondary"
                  onClick={this.load}
                  disabled={loading}
                  title="Refresh"
                >
                  <i
                    className={
                      'now-ui-icons loader_refresh' +
                      (loading ? ' spinner' : '')
                    }
                  />
                </button>
              )}
            </PageHeader>

            <div style={{ margin: '8px 0 16px' }}>
              <FilterBuilder
                key={builderKey}
                fields={REQUEST_FIELDS}
                initialConditions={conditions}
                initialLogicalOp={logicalOp}
                onChange={this.handleFilterChange}
                onSaveFilter={this.handleSaveFilter}
              />
            </div>

            {/* No filter: saved-filters panel as placeholder. */}
            {!hasFilter && (
              <FilterPanel
                inline
                resource="requests"
                fields={REQUEST_FIELDS}
                activeSavedFilter={activeSavedFilter}
                onApplySavedFilter={this.handleApplySavedFilter}
                onSavedFilterUpdated={this.handleSavedFilterUpdated}
                onDeletedSavedFilter={this.handleDeletedSavedFilter}
              />
            )}

            {/* Filter applied: results table + "load more" keyset pagination. */}
            {hasFilter && (
              <RequestTable
                rows={rows}
                loading={loading}
                error={error}
                onRetry={this.load}
                onRowClick={(row) => this.view(row.id)}
                footer={
                  hasResults && !loading && hasMore ? (
                    <div className="text-center">
                      <Button
                        color="primary"
                        outline
                        size="sm"
                        onClick={this.loadMore}
                        disabled={loadingMore}
                      >
                        {loadingMore ? 'Loading…' : 'Load more'}
                      </Button>
                    </div>
                  ) : null
                }
              />
            )}
          </Col>
        </Row>
      </div>
    );
  }
}

export default connect()(List);
